import { subscribeFn, unsubscribeFn } from "@/actions/push/handler";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const PUSH_CHANNEL = "pwa_push_sync";

type PermissionState = "default" | "granted" | "denied";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState<PermissionState>(
    typeof Notification !== "undefined"
      ? (Notification.permission as PermissionState)
      : "default",
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const channel = new BroadcastChannel(PUSH_CHANNEL);

    async function checkSubscription() {
      if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
        setIsLoading(false);
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        const subscribed = !!subscription;
        setIsSubscribed(subscribed);

        if (typeof Notification !== "undefined") {
          setPermission(Notification.permission as PermissionState);
        }
      } catch (error) {
        console.error("Error checking push subscription:", error);
      } finally {
        setIsLoading(false);
      }
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "SYNC_PUSH") {
        setIsSubscribed(event.data.isSubscribed);
        setPermission(event.data.permission);
      }
    };

    channel.addEventListener("message", handleMessage);

    // Initial check
    checkSubscription();

    // Re-check on focus
    window.addEventListener("focus", checkSubscription);

    return () => {
      channel.removeEventListener("message", handleMessage);
      channel.close();
      window.removeEventListener("focus", checkSubscription);
    };
  }, []);

  const syncState = (subscribed: boolean, perm: PermissionState) => {
    const channel = new BroadcastChannel(PUSH_CHANNEL);
    channel.postMessage({
      type: "SYNC_PUSH",
      isSubscribed: subscribed,
      permission: perm,
    });
    channel.close();
  };

  const requestPermission = async (): Promise<PermissionState> => {
    if (typeof Notification === "undefined") return "denied";
    const result = await Notification.requestPermission();
    setPermission(result as PermissionState);
    return result as PermissionState;
  };

  const subscribe = async () => {
    setIsLoading(true);
    try {
      const perm =
        permission === "default" ? await requestPermission() : permission;

      if (perm !== "granted") {
        if (perm === "denied") {
          toast.error("Bildirim izni reddedildi.");
        }
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidKey) throw new Error("VAPID public key not configured");

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      const keys = subscription.toJSON().keys;
      if (!keys?.p256dh || !keys?.auth)
        throw new Error("Invalid subscription keys");

      await subscribeFn({
        data: {
          endpoint: subscription.endpoint,
          p256dhKey: keys.p256dh,
          authKey: keys.auth,
        },
      });

      setIsSubscribed(true);
      setPermission("granted");
      syncState(true, "granted");
      toast.success("Bildirimler başarıyla etkinleştirildi!");
    } catch (error) {
      console.error("Push subscription failed:", error);
      toast.error("Bildirimler etkinleştirilemedi.");
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await unsubscribeFn({ data: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      syncState(false, permission);
      toast.success("Bildirimler devre dışı bırakıldı.");
    } catch (error) {
      console.error("Push unsubscription failed:", error);
      toast.error("Bildirimler devre dışı bırakılamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  return { permission, isSubscribed, isLoading, subscribe, unsubscribe };
}
